import { useState, useEffect } from 'react';
import { IonContent, IonHeader, IonPage, IonTitle, IonToolbar, IonButtons, IonBackButton, IonItem, IonToast, IonIcon } from '@ionic/react';
import { base64FromPath } from '@ionic/react-hooks/filesystem';
import { cameraOutline, trashOutline } from 'ionicons/icons';
import { Camera } from '@capacitor/camera';
import React from 'react';
import { UserState, userStore } from '../api/AuthService';
import { Claim } from '../api/model/Claim';
import {
  buildClaimPhotoFilename,
  CLAIM_PHOTO_USE_MOST_RECENT_CLOSED_CLAIM_FOR_TEST,
  getClaimDisplayFields,
  loadClaimsPageData,
  postClaimPhotoRecords,
  sendClaimPhotoEmails,
} from '../utils/ClaimUtils';
import { toastStore } from '../components/AppToast';

interface Photo {
  filepath: string;
  webviewPath?: string;
  data: any;
}

const Claims: React.FC<{ moduleHistory: string }> = ({ moduleHistory }) => {

  const { driver } = userStore((state: UserState) => ({ driver: state.driver }));

  const [mostRecentClaim, setMostRecentClaim] = useState<Claim | null>(null);
  const [relatedClaimNumbers, setRelatedClaimNumbers] = useState<string[]>([]);
  const [relatedClaimIds, setRelatedClaimIds] = useState<number[]>([]);
  const [photoSubmissionRequired, setPhotoSubmissionRequired] = useState(false);
  const claimDisplayFields = getClaimDisplayFields(mostRecentClaim);

  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [images, setImages] = useState<Photo[]>([]);

  useEffect(() => {
    if (!driver?.unit) {
      return;
    }

    loadClaimsPageData('claims', driver.unit, driver.driverCode)
      .then(({ claimGroup, photoSubmissionRequired: submissionRequired }) => {
        setMostRecentClaim(claimGroup.mostRecentClaim);
        setRelatedClaimNumbers(claimGroup.relatedClaimNumbers);
        setRelatedClaimIds(claimGroup.relatedClaimIds);
        setPhotoSubmissionRequired(submissionRequired);
      })
      .catch((error) => {
        console.log('[ClaimsPhoto] Claims page load error', error);
        setMostRecentClaim(null);
        setRelatedClaimNumbers([]);
        setRelatedClaimIds([]);
        setPhotoSubmissionRequired(false);
      });
  }, [driver?.unit, driver?.driverCode]);

  enum CameraResultType { Uri = 'uri', Base64 = 'base64', DataUrl = 'dataUrl' }

  async function submitClaimPhotos() {
    if (images.length < 1 || relatedClaimIds.length < 1 || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    try {
      const claimNumbersForEmail = relatedClaimNumbers.length
        ? relatedClaimNumbers
        : relatedClaimIds.map(String);

      console.log('[ClaimsPhoto] submitClaimPhotos start', {
        claimNumbersForEmail,
        relatedClaimIds,
        photoCount: images.length,
      });

      await sendClaimPhotoEmails({
        relatedClaimNumbers: claimNumbersForEmail,
        photos: images,
        driver: {
          name: driver?.name,
          driverCode: driver?.driverCode,
          unit: driver?.unit,
          trailer: driver?.trailer,
          division: driver?.division,
          dmName: driver?.dmName,
        },
        incidentTime: mostRecentClaim?.incidentTime,
      });

      await postClaimPhotoRecords(
        'claims',
        relatedClaimIds,
        images.length,
        driver?.driverCode,
        driver?.unit,
      );

      console.log('[ClaimsPhoto] submitClaimPhotos complete');
      setSubmitSuccess(true);
      setImages([]);
      setPhotoSubmissionRequired(false);
    } catch (err: any) {
      console.log('[ClaimsPhoto] submitClaimPhotos error', err);
      const message = typeof err === 'string'
        ? err
        : err?.errmsg ?? err?.message ?? 'Failed to submit claim photos.';
      toastStore.getState().setToast(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  function getClaimNumberForFilename(): string {
    return String(relatedClaimNumbers[0] ?? mostRecentClaim?.claimNumber ?? 'claim');
  }

  async function takePicture() {
    const image = await Camera.getPhoto({ quality: 100, allowEditing: false, resultType: CameraResultType.Uri });
    const base64Data = await base64FromPath(image.webPath!);
    const claimNumber = getClaimNumberForFilename();
    setImages(prev => {
      const fileName = buildClaimPhotoFilename(claimNumber, prev.length + 1);
      return [...prev, { filepath: fileName, webviewPath: image.webPath, data: base64Data }];
    });
  }

  const removePhoto = (photo: Photo) => {
    const claimNumber = getClaimNumberForFilename();
    setImages(prev => prev
      .filter(img => img.filepath !== photo.filepath)
      .map((img, index) => ({
        ...img,
        filepath: buildClaimPhotoFilename(claimNumber, index + 1),
      })));
  };

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar color="primary">
          <IonButtons slot="start">
            <IonBackButton defaultHref="/Home" text="" />
          </IonButtons>
          <IonTitle>Claims</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonToast
        isOpen={submitSuccess}
        onDidDismiss={() => setSubmitSuccess(false)}
        message="Your claim photos were submitted successfully."
        color="success"
        duration={3000}
      />

      <IonContent color="white">
        <div className='main-2'>
          <div>
            <IonItem color="white" lines="none">
              <div className='item-container'>
                  <h1>Submit Claim Photos</h1>

                  {CLAIM_PHOTO_USE_MOST_RECENT_CLOSED_CLAIM_FOR_TEST && (
                    <div style={{ background: '#fff3cd', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: '#856404' }}>
                      Test mode: using most recent closed claim for photo submission.
                    </div>
                  )}

                  {photoSubmissionRequired && (
                    <div style={{ background: '#fdecea', borderRadius: '10px', padding: '10px 14px', marginBottom: '14px', fontSize: '14px', color: '#b71c1c', fontWeight: 600 }}>
                      You need to submit claim photos.
                    </div>
                  )}

                  <div style={{ background: "#f5f5f5", borderRadius: "10px", padding: "12px 16px", marginBottom: "14px", fontSize: "14px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                      <div><span style={{ color: "#888", fontWeight: 600 }}>Unit #: </span>{driver?.unit || "—"}</div>
                      {claimDisplayFields.map(({ key, label, value }) => (
                        <div key={key}><span style={{ color: "#888", fontWeight: 600 }}>{label}: </span>{value}</div>
                      ))}
                    </div>
                  </div>

                  <div id="requiredPhotosSection" style={{ background: '#f5f5f5', borderRadius: '10px', padding: '12px 16px', marginBottom: '14px', fontSize: '14px' }}>
                    <p style={{ margin: '0 0 8px', fontWeight: 700 }}>Photos Requirements:</p>
                    <ul style={{ textAlign: 'left', paddingLeft: '20px', margin: 0 }}>
                      <li>Photo of all sides of the truck</li>
                      <li>Photo of the damage(s)</li>
                      <li>Photo of the truck's VIN# on the truck/door (Identifies the truck)</li>
                      <li>Photo of the truck's Mileage.</li>
                    </ul>
                  </div>
              </div>
            </IonItem>
          </div>

          <div>
            <div style={{ display: "grid", gridTemplateColumns: '1fr', padding: "8px", backgroundColor: "#ffffff", margin: "auto", textAlign: "center" }}>
              {images.map((img, index) => (
                <div key={index} onClick={() => removePhoto(img)} style={{ position: "relative" }}>
                  <img alt="claim photo" style={{ width: "200px", borderRadius: "10px" }} src={img.webviewPath} />
                  <button style={{ backgroundColor: "rgba(0,0,0,0)", borderRadius: "50%", width: "42px", height: "42px", position: "absolute", top: "0", left: "0" }}>
                    <IonIcon style={{ color: "#ff0000", fontSize: "xx-large" }} icon={trashOutline} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div id="ClaimsBottom">
          <button className='button' style={{ margin: "18px auto" }} onClick={() => takePicture()}>
            <p>{images.length < 1 ? "Take Photo" : "Take Another Photo"}</p>
            <IonIcon icon={cameraOutline} />
          </button>

          <IonItem lines="none" color="white">
            <div className='item-container'>
              <button className="western-button submit" disabled={images.length < 1 || relatedClaimIds.length < 1 || isSubmitting} onClick={() => submitClaimPhotos()}>
                SUBMIT CLAIM PHOTOS
              </button>
            </div>
          </IonItem>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Claims;
